/**
 * Authentication Logger Utility
 * 
 * Provides functionality to log authentication events like login, logout,
 * and track session duration.
 */

import axios from 'axios';

// Constants
const SERVICE_NAME = 'login';
const API_ENDPOINT = '/nodeapi/logs'; // Updated to the HTTP endpoint

// Types
interface LogPayload {
  level: 'info' | 'debug' | 'error';
  serviceName: string;
  message: {
    action: string;
    description: string;
    username?: string;
    ipAddress?: string;
    timestamp: string;
    sessionDuration?: number;
    userAgent?: string;
    additionalInfo?: Record<string, any>;
  };
}

/**
 * Get user's IP address
 * Uses a public API to get the client's IP address
 */
const getClientIP = async (): Promise<string> => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('Failed to get IP address:', error);
    return 'unknown';
  }
};

/**
 * Send log to the backend
 */
const sendLog = async (payload: LogPayload): Promise<void> => {
  try {
    const token = localStorage.getItem('token');
    
    await axios.post(API_ENDPOINT, payload, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
  } catch (error) {
    console.error('Failed to send log:', error);
  }
};

/**
 * Log login event
 */
export const logLogin = async (username: string): Promise<void> => {
  const ipAddress = await getClientIP();
  
  // Store login time in localStorage for session duration tracking
  const loginTime = new Date().toISOString();
  localStorage.setItem('auth_login_time', loginTime);
  
  const payload: LogPayload = {
    level: 'info',
    serviceName: SERVICE_NAME,
    message: {
      action: 'login',
      description: 'User logged in',
      username,
      ipAddress,
      timestamp: loginTime,
      userAgent: navigator.userAgent,
    }
  };
  
  await sendLog(payload);
};

/**
 * Log logout event using Navigator.sendBeacon to ensure delivery even during page redirects
 */
export const logLogout = async (username: string): Promise<void> => {
  const ipAddress = await getClientIP();
  
  // Calculate session duration
  const loginTime = localStorage.getItem('auth_login_time');
  let sessionDuration: number | undefined;
  
  if (loginTime) {
    const startTime = new Date(loginTime).getTime();
    const endTime = new Date().getTime();
    sessionDuration = Math.round((endTime - startTime) / 1000); // Duration in seconds
  }
  
  const payload: LogPayload = {
    level: 'info',
    serviceName: SERVICE_NAME,
    message: {
      action: 'logout',
      description: 'User logged out',
      username,
      ipAddress,
      timestamp: new Date().toISOString(),
      sessionDuration,
      userAgent: navigator.userAgent,
    }
  };
  
  // Use sendBeacon for reliable delivery during page unload/redirect
  if (navigator.sendBeacon) {
    const token = localStorage.getItem('token');
    const blob = new Blob(
      [JSON.stringify(payload)], 
      { type: 'application/json' }
    );
    
    // Create a URL with the token in the query string to avoid CORS issues with Beacon
    const url = `${API_ENDPOINT}?token=${encodeURIComponent(token || '')}`;
    
    // sendBeacon returns boolean - true if successfully queued
    const result = navigator.sendBeacon(url, blob);
    
    if (!result) {
      console.error('Failed to queue logout beacon');
      // Fall back to standard request as backup
      try {
        await sendLog(payload);
      } catch (error) {
        console.error('Fallback logging failed:', error);
      }
    }
  } else {
    // Fall back to standard request if sendBeacon not available
    try {
      await sendLog(payload);
    } catch (error) {
      console.error('Failed to send log:', error);
    }
  }
  
  // Clear stored login time
  localStorage.removeItem('auth_login_time');
};

/**
 * Log session information
 * Useful for tracking session activity at regular intervals
 */
export const logSessionInfo = async (username: string, additionalInfo?: Record<string, any>): Promise<void> => {
  const ipAddress = await getClientIP();
  
  // Calculate current session duration
  const loginTime = localStorage.getItem('auth_login_time');
  let sessionDuration: number | undefined;
  
  if (loginTime) {
    const startTime = new Date(loginTime).getTime();
    const currentTime = new Date().getTime();
    sessionDuration = Math.round((currentTime - startTime) / 1000); // Duration in seconds
  }
  
  const payload: LogPayload = {
    level: 'debug',
    serviceName: SERVICE_NAME,
    message: {
      action: 'session_info',
      description: 'User session information',
      username,
      ipAddress,
      timestamp: new Date().toISOString(),
      sessionDuration,
      userAgent: navigator.userAgent,
      additionalInfo
    }
  };
  
  await sendLog(payload);
};

export default {
  logLogin,
  logLogout,
  logSessionInfo
};
